import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createMinister as createMinisterService,
  updateMinister as updateMinisterService,
  deleteMinister as deleteMinisterService,
  checkMinisterLinks,
  hardDeleteMinister as hardDeleteMinisterService,
  type MinisterLinkedRecords,
} from "@/data/ministerRepository";
import { supabase } from "@/supabase.config";
import type { Minister } from "@/models/minister";
import type { MinisterFormData } from "../types";
import { Database } from "@/types/supabase";
import { generateFullName } from "../utils/validations";
import { fileToArrayBuffer, arrayBufferToHex, constructFilename } from "../utils/imageProcessing";
import { ALL_MINISTERS_QUERY_KEY } from "./useAllMinisters";
import { REPORT_QUERY_KEYS } from "@/features/reports/queryKeys";

type MinisterInsert = Database['public']['Tables']['ministers']['Insert'];
type MinisterUpdate = Database['public']['Tables']['ministers']['Update'];

/**
 * Hook to manage minister CRUD operations
 */
export function useMinister() {
  const queryClient = useQueryClient();

  /** Invalidate all report caches that use Infinity staleTime (roster-level data). */
  const invalidateReportCaches = () => {
    Object.values(REPORT_QUERY_KEYS).forEach((key) =>
      queryClient.invalidateQueries({ queryKey: key })
    );
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingLinks, setIsCheckingLinks] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Uploads photo to Supabase Storage and updates minister record with binary data
   * @param processedPhotoFile - Resized/compressed file for web display (Storage)
   * @param originalPhotoFile - Original unprocessed file for CardPresso (Database)
   */
  const uploadPhotoToStorageAndDB = useCallback(async (
    adbCode: string,
    processedPhotoFile: File,
    originalPhotoFile: File,
    ministerId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // 1. Construct filename based on adb_code and original extension
      const filename = constructFilename(adbCode, processedPhotoFile.name);
      const storagePath = `ProfilePhotos/${filename}`;

      // 2. Upload PROCESSED file to Supabase Storage for web display (overwrites if exists)
      const { error: storageError } = await supabase.storage
        .from('MinisterImages')
        .upload(storagePath, processedPhotoFile, {
          upsert: true,
          contentType: processedPhotoFile.type,
        });

      if (storageError) {
        console.error('Storage upload error:', storageError);
        return { success: false, error: 'Error al subir la foto al almacenamiento' };
      }

      // 3. Convert ORIGINAL file to binary for database (for CardPresso compatibility)
      const binaryData = await fileToArrayBuffer(originalPhotoFile);
      const hexString = arrayBufferToHex(binaryData);

      // 4. Update minister record with binary data (as hex string) and filename
      const updateData: MinisterUpdate = {
        profile_picture: hexString as unknown as MinisterUpdate['profile_picture'],
        profile_picture_filename: filename,
      };

      const { error: dbError } = await supabase
        .from('ministers')
        .update(updateData as never)
        .eq('id', ministerId);

      if (dbError) {
        console.error('Database update error:', dbError);
        return { success: false, error: 'Error al guardar la foto en la base de datos' };
      }

      return { success: true };
    } catch (error) {
      console.error('Photo upload error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Error al procesar la foto' 
      };
    }
  }, []);

  /**
   * Create a new minister
   */
  const createMinister = useCallback(
    async (
      formData: MinisterFormData, 
      userId: string, 
      processedPhotoFile?: File | null,
      originalPhotoFile?: File | null
    ): Promise<{ minister: Minister | null; photoError?: string }> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Generate full_name from name components
        const fullName = generateFullName(
          formData.first_name,
          formData.last_name,
          formData.second_last_name || null,
          formData.marriage_last_name || null
        );

        const ministerData: MinisterInsert = {
          adb_code: formData.adb_code.trim(),
          full_name: fullName,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          second_last_name: formData.second_last_name?.trim() || null,
          marriage_last_name: formData.marriage_last_name?.trim() || null,
          dni: formData.dni.trim(),
          degree_id: formData.degree_id || null,
          district_id: formData.district_id || null,
          region_id: formData.region_id || null,
          position: formData.position?.trim() || null,
          email: formData.email?.trim() || null,
          cell_phone: formData.cell_phone?.trim() || null,
          office_phone: formData.office_phone?.trim() || null,
          address: formData.address?.trim() || null,
          address_city: formData.address_city?.trim() || null,
          address_zone: formData.address_zone?.trim() || null,
          is_active: true,
          created_by: userId,
        };

        const newMinister = await createMinisterService(ministerData);
        
        if (!newMinister) {
          throw new Error("Error al crear el ministro");
        }

        // Upload photo if provided
        let photoError: string | undefined;
        if (processedPhotoFile && originalPhotoFile && newMinister.id) {
          const uploadResult = await uploadPhotoToStorageAndDB(
            formData.adb_code.trim(),
            processedPhotoFile,
            originalPhotoFile,
            newMinister.id
          );
          if (!uploadResult.success) {
            photoError = uploadResult.error;
          }
        }

        queryClient.invalidateQueries({ queryKey: ALL_MINISTERS_QUERY_KEY });
        invalidateReportCaches();
        return { minister: newMinister, photoError };
      } catch (err) {
        console.error("Error creating minister:", err);
        setError(err instanceof Error ? err.message : "Error al crear el ministro");
        return { minister: null };
      } finally {
        setIsSubmitting(false);
      }
    },
    [uploadPhotoToStorageAndDB, queryClient]
  );

  /**
   * Update an existing minister
   */
  const updateMinister = useCallback(
    async (
      ministerId: string,
      formData: MinisterFormData,
      userId: string,
      processedPhotoFile?: File | null,
      originalPhotoFile?: File | null,
      originalMinister?: Minister | null,
      shouldDeletePhoto?: boolean
    ): Promise<{ minister: Minister | null; photoError?: string }> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Generate full_name from name components
        const fullName = generateFullName(
          formData.first_name,
          formData.last_name,
          formData.second_last_name || null,
          formData.marriage_last_name || null
        );

        const ministerData: MinisterUpdate = {
          adb_code: formData.adb_code.trim(),
          full_name: fullName,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          second_last_name: formData.second_last_name?.trim() || null,
          marriage_last_name: formData.marriage_last_name?.trim() || null,
          dni: formData.dni.trim(),
          degree_id: formData.degree_id || null,
          district_id: formData.district_id || null,
          region_id: formData.region_id || null,
          position: formData.position?.trim() || null,
          email: formData.email?.trim() || null,
          cell_phone: formData.cell_phone?.trim() || null,
          office_phone: formData.office_phone?.trim() || null,
          address: formData.address?.trim() || null,
          address_city: formData.address_city?.trim() || null,
          address_zone: formData.address_zone?.trim() || null,
          updated_by: userId,
        };

        const updatedMinister = await updateMinisterService(ministerId, ministerData);
        
        if (!updatedMinister) {
          throw new Error("Error al actualizar el ministro");
        }

        // Handle photo operations
        let photoError: string | undefined;

        // Handle photo deletion if requested
        if (shouldDeletePhoto && originalMinister?.profile_picture_filename) {
          try {
            const photoPath = `ProfilePhotos/${originalMinister.profile_picture_filename}`;
            
            // Delete from storage
            const { error: deleteError } = await supabase
              .storage
              .from('MinisterImages')
              .remove([photoPath]);

            if (deleteError) console.warn('Failed to delete photo from storage:', deleteError);

            // Clear photo fields in database
            const { error: dbError } = await supabase
              .from('ministers')
              .update({ 
                profile_picture: null,
                profile_picture_filename: null 
              } as never)
              .eq('id', ministerId);

            if (dbError) {
              console.error('Failed to clear photo in DB:', dbError);
              photoError = 'Error al eliminar la foto de la base de datos';
            }
          } catch (error) {
            console.error('Error deleting photo:', error);
            photoError = 'Error al eliminar la foto';
          }
        }

        // Check if adb_code changed and there's an existing photo
        if (originalMinister && 
            originalMinister.adb_code !== formData.adb_code.trim() && 
            originalMinister.profile_picture_filename) {
          
          // Rename the file in storage
          const oldFilename = originalMinister.profile_picture_filename;
          const extension = oldFilename.substring(oldFilename.lastIndexOf('.'));
          const newFilename = `${formData.adb_code.trim()}${extension}`;
          const oldPath = `ProfilePhotos/${oldFilename}`;
          const newPath = `ProfilePhotos/${newFilename}`;

          try {
            // Copy to new location
            const { error: copyError } = await supabase
              .storage
              .from('MinisterImages')
              .copy(oldPath, newPath);

            if (copyError) throw copyError;

            // Delete old file
            const { error: deleteError } = await supabase
              .storage
              .from('MinisterImages')
              .remove([oldPath]);

            if (deleteError) console.warn('Failed to delete old photo:', deleteError);

            // Update filename in database
            const { error: dbError } = await supabase
              .from('ministers')
              .update({ profile_picture_filename: newFilename } as never)
              .eq('id', ministerId);

            if (dbError) {
              console.error('Failed to update filename in DB:', dbError);
              photoError = 'Error al actualizar el nombre de la foto';
            }
          } catch (error) {
            console.error('Error renaming photo file:', error);
            photoError = 'Error al renombrar la foto';
          }
        }

        // Upload new photo if provided
        if (processedPhotoFile && originalPhotoFile) {
          const uploadResult = await uploadPhotoToStorageAndDB(
            formData.adb_code.trim(),
            processedPhotoFile,
            originalPhotoFile,
            ministerId
          );
          if (!uploadResult.success) {
            photoError = uploadResult.error;
          }
        }

        queryClient.invalidateQueries({ queryKey: ALL_MINISTERS_QUERY_KEY });
        invalidateReportCaches();
        return { minister: updatedMinister, photoError };
      } catch (err) {
        console.error("Error updating minister:", err);
        setError(err instanceof Error ? err.message : "Error al actualizar el ministro");
        return { minister: null };
      } finally {
        setIsSubmitting(false);
      }
    },
    [uploadPhotoToStorageAndDB, queryClient]
  );

  /**
   * Soft delete a minister (sets is_active = false)
   */
  const deleteMinister = useCallback(async (ministerId: string): Promise<boolean> => {
    setIsDeleting(true);
    setError(null);

    try {
      const success = await deleteMinisterService(ministerId);

      if (!success) {
        throw new Error("Error al eliminar el ministro");
      }

      queryClient.invalidateQueries({ queryKey: ALL_MINISTERS_QUERY_KEY });
      invalidateReportCaches();
      return true;
    } catch (err) {
      console.error("Error deleting minister:", err);
      setError(err instanceof Error ? err.message : "Error al eliminar el ministro");
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [queryClient]);

  const getMinisterLinkedRecords = useCallback(async (ministerId: string): Promise<MinisterLinkedRecords | null> => {
    setIsCheckingLinks(true);
    try {
      return await checkMinisterLinks(ministerId);
    } catch (err) {
      console.error("Error checking minister links:", err);
      return null;
    } finally {
      setIsCheckingLinks(false);
    }
  }, []);

  const hardDeleteMinister = useCallback(async (ministerId: string): Promise<boolean> => {
    setIsDeleting(true);
    setError(null);
    try {
      const success = await hardDeleteMinisterService(ministerId);
      if (!success) throw new Error("Error al eliminar el ministro");
      queryClient.invalidateQueries({ queryKey: ALL_MINISTERS_QUERY_KEY });
      invalidateReportCaches();
      return true;
    } catch (err) {
      console.error("Error hard deleting minister:", err);
      setError(err instanceof Error ? err.message : "Error al eliminar el ministro");
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [queryClient]);

  return {
    createMinister,
    updateMinister,
    deleteMinister,
    getMinisterLinkedRecords,
    hardDeleteMinister,
    isSubmitting,
    isDeleting,
    isCheckingLinks,
    error,
  };
}
